import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styles from './bodywon.module.css';
import { createProductReviewApi } from '../utils/productReviewApi';
import { ensureLoggedInUserId } from '../utils/cartApi';

const WriteReviewModal = ({ show, onHide, product, initialRating = 5, onSubmitted }) => {
  const [rating, setRating] = useState(initialRating);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setRating(initialRating);
      setHoverRating(0);
      setTitle('');
      setText('');
    }
  }, [show, initialRating, product?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!product?.id) return;

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.warning('Please enter a review title.');
      return;
    }

    const userId = await ensureLoggedInUserId();
    if (!userId) {
      toast.warning('Please login to write a review.');
      onHide();
      return;
    }

    setSaving(true);
    try {
      await createProductReviewApi({
        ProductId: Number(product.id),
        UserId: userId,
        Rating: rating,
        ReviewTitle: trimmedTitle,
        ReviewText: text.trim() || null,
      });
      toast.success('Thank you! Your review was submitted.');
      onSubmitted?.();
      onHide();
    } catch (err) {
      toast.error(err.message || 'Could not submit review.');
    } finally {
      setSaving(false);
    }
  };

  const activeRating = hoverRating || rating;

  const stopBubble = (e) => {
    e.stopPropagation();
  };

  const modal = (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={styles.reviewModal}
      onMouseDown={stopBubble}
      onClick={stopBubble}
    >
      <Modal.Header closeButton>
        <Modal.Title className={styles.reviewModalTitle}>Write a Review</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className={`${styles.reviewProductInfo} mb-4`}>
            <p className="mb-0">{product?.name}</p>
          </div>

          <div className="mb-4">
            <Form.Label>Your rating</Form.Label>
            <div className={styles.reviewModalStars} role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={styles.reviewStarBtn}
                  onMouseEnter={() => setHoverRating(value)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(value)}
                  aria-label={`${value} star${value > 1 ? 's' : ''}`}
                >
                  {value <= activeRating ? <FaStar /> : <FaRegStar />}
                </button>
              ))}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Review title</Form.Label>
            <Form.Control
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience"
              maxLength={300}
              required
            />
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label>Review</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell others what you liked about this product"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button type="submit" className={styles.submitReviewBtn} disabled={saving}>
            {saving ? 'Submitting...' : 'Submit Review'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modal, document.body);
};

export default WriteReviewModal;
